/**
 * House Allocation Page
 * Split-panel layout: unassigned students (left) + house grid (right).
 * Gender tabs on the right auto-filter the left panel to matching gender.
 * HOUSE_OWNER users see the board in read-only mode.
 */

import React, { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { api, apiClient } from '../../lib/api';
import { useHouseAllocation } from '../../hooks/useHouseAllocation';
import { useCourses } from '../../hooks/useCourses';
import { useCourseTime } from '../../contexts/CourseTimeContext';
import { useUserPermissions } from '../../hooks/useAuth';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CourseTimeToggle } from '../ui/course-time-toggle';
import { HousePrintView } from '../students/HousePrintView';

import { Loader2, Users, UsersRound, AlertCircle, Trash2, X, ChevronLeft, Award, UserCog, Download, Printer, Search } from 'lucide-react';

// ─── Occupancy bar ────────────────────────────────────────────────────────────

function OccupancyBar({ current, max }) {
  const pct = max > 0 ? (current / max) * 100 : 0;
  const isOver = current > max;
  const isFull = !isOver && pct >= 100;
  const isInProgress = !isOver && !isFull && pct > 0;
  return (
    <div className="h-1.5 rounded-full bg-muted flex-1">
      <div
        className={`h-1.5 rounded-full transition-all ${isOver ? 'bg-red-500' : isFull ? 'bg-green-500' : ''}`}
        style={{
          width: `${Math.min(pct, 100)}%`,
          ...(isInProgress ? { background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' } : {}),
        }}
      />
    </div>
  );
}

// ─── Student card ─────────────────────────────────────────────────────────────

function StudentCard({ student, studentLookup = {}, isDragging = false, readOnly = false, highlighted = false }) {
  const { t } = useTranslation();
  const isMale = student.gender === 'M';
  const hasGroup = student.friend_group && student.friend_group.length > 0;
  const groupSize = hasGroup ? student.friend_group.length + 1 : 0;

  const cardColor = student.is_cuntoir
    ? 'bg-teal-50 border-teal-300 dark:bg-teal-900/30 dark:border-teal-700'
    : student.is_cinnire
      ? 'bg-amber-50 border-amber-300 dark:bg-amber-900/30 dark:border-amber-700'
      : isMale
        ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/30 dark:border-blue-800'
        : 'bg-pink-50 border-pink-200 dark:bg-pink-900/30 dark:border-pink-800';

  return (
    <div
      className={`
        group rounded-md border px-3 py-2 text-sm select-none
        ${isDragging ? 'opacity-50' : 'shadow-sm'}
        ${cardColor}
        ${student._crossYearFriend ? 'border-dashed' : ''}
        ${readOnly ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'}
        ${highlighted ? 'ring-2 ring-amber-400' : ''}
      `}
    >
      <div className="flex items-start justify-between gap-1">
        <span className="font-medium leading-tight">
          {student.first_name} {student.last_name}
          {student.age != null && <span className="font-normal"> - {student.age}</span>}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {student.is_cuntoir && (
            <UserCog className="h-3.5 w-3.5 text-teal-600 flex-shrink-0" />
          )}
          {student.is_cinnire && (
            <Award className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
          )}
          {hasGroup && (
            <span className="inline-flex items-center gap-1 bg-purple-600 text-white text-xs px-1.5 py-0.5 rounded-full">
              <Users className="h-3 w-3" />
              {groupSize}
            </span>
          )}
        </div>
      </div>
      <div className="text-xs text-muted-foreground mt-0.5">{student.student_id}</div>
      {(student.school || student.school_year) && (
        <div className="text-xs text-muted-foreground mt-0.5 truncate">
          {[student.school, student.school_year].filter(Boolean).join(' · ')}
        </div>
      )}
      {student.home_county && (
        <div className="text-xs text-muted-foreground">{student.home_county}</div>
      )}
      {student._crossYearFriend && (
        <div className="mt-1 inline-flex items-center gap-0.5 text-xs text-purple-600 dark:text-purple-400">
          <Users className="h-3 w-3" />
          {t('houseAllocation.crossYearFriend')}
        </div>
      )}
      {hasGroup && (
        <div className="hidden group-hover:block mt-1.5 pt-1.5 border-t border-purple-200 space-y-0.5">
          {student.friend_group.map((uuid) => {
            const member = studentLookup[uuid];
            return member ? (
              <div key={uuid} className="text-xs text-purple-700">
                {member.first_name} {member.last_name} · {member.student_id}
              </div>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
}

// ─── Draggable wrapper ────────────────────────────────────────────────────────

function DraggableStudent({ student, studentLookup, readOnly, highlighted = false }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: student.uuid,
    disabled: readOnly,
  });

  return (
    <div ref={setNodeRef} {...listeners} {...attributes}>
      <StudentCard
        student={student}
        studentLookup={studentLookup}
        isDragging={isDragging}
        readOnly={readOnly}
        highlighted={highlighted}
      />
    </div>
  );
}

// ─── Unassigned left panel ────────────────────────────────────────────────────

function UnassignedPanel({ students, totalCount, personTypeFilter, onPersonTypeFilter, allPeople, activeGender, readOnly, studentLookup, yearFilter, onYearFilter, yearOptions }) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: 'unassigned' });

  // Per-type counts for the active gender (unassigned only)
  const typeCounts = useMemo(() => {
    const base = allPeople.filter((s) => !s.house_uuid && s.gender === activeGender);
    return {
      all:     base.length,
      student: base.filter((s) => !s.is_cinnire && !s.is_cuntoir).length,
      cinnire: base.filter((s) => s.is_cinnire).length,
      cuntoir: base.filter((s) => s.is_cuntoir).length,
    };
  }, [allPeople, activeGender]);

  const TYPE_FILTERS = [
    { key: 'all',     label: t('common.all', 'All'),    className: 'bg-muted text-foreground border-border' },
    { key: 'student', label: t('houseAllocation.students', 'Students'), className: 'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700' },
    { key: 'cinnire', label: t('houseAllocation.cinnire', 'Cinnirí'),   className: 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700' },
    { key: 'cuntoir', label: t('houseAllocation.cuntoir', 'Cúntóirí'), className: 'bg-teal-50 text-teal-700 border-teal-300 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-700' },
  ].filter((f) => f.key === 'all' || typeCounts[f.key] > 0);

  return (
    <div className="w-full md:w-72 md:flex-shrink-0 flex flex-col rounded-lg border bg-muted/30 min-h-0">
      {/* Header */}
      <div className="px-3 pt-3 pb-2 border-b flex-shrink-0">
        <div className="flex items-center justify-between">
          <span className="font-medium text-sm">{t('houseAllocation.unassigned')}</span>
          <Badge variant="secondary">{totalCount}</Badge>
        </div>
        {/* Type filter pills */}
        <div className="flex flex-wrap gap-1 mt-2">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => onPersonTypeFilter(f.key)}
              className={`inline-flex items-center gap-1 px-2 py-2 rounded-full text-xs border transition-colors ${
                personTypeFilter === f.key
                  ? f.className
                  : 'bg-transparent text-muted-foreground border-transparent hover:border-border'
              }`}
            >
              {f.label}
              <span className="opacity-70">{typeCounts[f.key]}</span>
            </button>
          ))}
        </div>

        {/* Year filter */}
        {yearOptions.length > 1 && (
          <Select value={yearFilter} onValueChange={onYearFilter}>
            <SelectTrigger className="h-10 text-xs mt-2">
              <SelectValue placeholder={t('houseAllocation.filterAllYears')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('houseAllocation.filterAllYears')}</SelectItem>
              {yearOptions.map((y) => (
                <SelectItem key={y.year} value={y.year}>{y.year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Student list — droppable */}
      <div
        ref={setNodeRef}
        className={`flex-1 overflow-y-auto p-2 space-y-1.5 min-h-[120px] transition-colors ${
          isOver && !readOnly ? 'bg-primary/5' : ''
        }`}
      >
        {students.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-6">
            {t('houseAllocation.noStudents')}
          </div>
        ) : (
          students.map((s) => (
            <DraggableStudent key={s.uuid} student={s} studentLookup={studentLookup} readOnly={readOnly} />
          ))
        )}
      </div>
    </div>
  );
}

// ─── House card (grid item) ───────────────────────────────────────────────────

function HouseCard({ house, students, isSelected, onSelect, readOnly, matchCount = 0, searchActive = false }) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: house.uuid });
  const isFull = students.length >= house.course_capacity;
  const countColor = 'text-foreground';
  const hasMatch = matchCount > 0;

  // Derived stats from assigned students
  const stats = useMemo(() => {
    if (!students.length) return null;
    const ages = students.map((s) => s.age).filter((a) => a != null);
    const minAge = ages.length ? Math.min(...ages) : null;
    const maxAge = ages.length ? Math.max(...ages) : null;
    const ageRange = minAge != null
      ? minAge === maxAge ? `${minAge}` : `${minAge} – ${maxAge}`
      : null;
    const schoolYears = [...new Set(students.map((s) => s.school_year).filter(Boolean))];
    const groupCount = new Set(
      students.filter((s) => s.friend_group.length > 0).map((s) => {
        const ids = [s.uuid, ...s.friend_group].sort().join('-');
        return ids;
      })
    ).size;
    return { ageRange, schoolYears, groupCount };
  }, [students]);

  return (
    <div
      ref={setNodeRef}
      className={`
        rounded-lg border bg-card transition-colors cursor-pointer
        ${isOver && !readOnly ? 'border-primary ring-1 ring-primary bg-primary/5' :
          hasMatch ? 'border-amber-400 ring-1 ring-amber-400 bg-amber-50/30 dark:bg-amber-900/10' :
          isSelected ? 'border-blue-400 ring-1 ring-blue-400 bg-blue-50/40 dark:bg-blue-900/20' :
          'border-border hover:border-border/80 hover:bg-muted/20'}
        ${searchActive && !hasMatch ? 'opacity-40' : isFull ? 'opacity-70' : ''}
      `}
      onClick={onSelect}
    >
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-medium text-sm truncate">{house.name}</span>
          {house.id && <span className="text-xs text-muted-foreground flex-shrink-0">#{house.id}</span>}
          {students.some((s) => s.is_cinnire) && (
            <Award className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
          )}
          {students.some((s) => s.is_cuntoir) && (
            <UserCog className="h-3.5 w-3.5 text-teal-600 flex-shrink-0" />
          )}
          {hasMatch && (
            <span className="ml-auto flex-shrink-0 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-xs font-semibold px-1.5 py-0.5 rounded-full">
              {matchCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <OccupancyBar current={students.length} max={house.course_capacity} />
          <span className={`text-xs flex-shrink-0 ${countColor}`}>
            {students.length}/{house.course_capacity}
            {isFull && (
              <span className="ml-1 font-medium">· {t('houseAllocation.full')}</span>
            )}
          </span>
        </div>
        {stats && (
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {stats.ageRange && (
              <span className="text-xs text-muted-foreground">{stats.ageRange}</span>
            )}
            {stats.schoolYears.length > 0 && (
              <>
                <span className="text-xs text-muted-foreground">·</span>
                <span className="text-xs text-muted-foreground">{stats.schoolYears.join(' · ')}</span>
              </>
            )}
            {stats.groupCount > 0 && (
              <>
                <span className="text-xs text-muted-foreground">·</span>
                <span className="inline-flex items-center gap-1 text-xs text-purple-600">
                  <UsersRound className="h-3 w-3" />
                  {stats.groupCount}
                </span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── House detail panel ───────────────────────────────────────────────────────

function HouseDetailPanel({ house, students, onClose, readOnly, studentLookup, searchQuery = '' }) {
  const { t } = useTranslation();
  const isFull = students.length >= house.course_capacity;
  const countColor = 'text-foreground';

  const matchUuids = useMemo(() => {
    if (!searchQuery) return new Set();
    const q = searchQuery.toLowerCase();
    return new Set(
      students
        .filter((s) => {
          const name = `${s.first_name} ${s.last_name}`.toLowerCase();
          return name.includes(q) || (s.student_id || '').includes(q);
        })
        .map((s) => s.uuid)
    );
  }, [students, searchQuery]);

  return (
    <div className="w-full md:w-72 md:flex-shrink-0 flex flex-col rounded-lg border border-blue-300 dark:border-blue-700 bg-card shadow-sm min-h-0">
      {/* Header */}
      <div className="px-3 pt-3 pb-2 border-b flex-shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="font-semibold text-sm truncate">{house.name}</span>
              {house.id && <span className="text-xs text-muted-foreground flex-shrink-0">#{house.id}</span>}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <OccupancyBar current={students.length} max={house.course_capacity} />
              <span className={`text-xs flex-shrink-0 ${countColor}`}>
                {students.length}/{house.course_capacity}
                {isFull && <span className="ml-1 font-medium">· {t('houseAllocation.full')}</span>}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Student list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5 min-h-[120px]">
        {students.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-6">
            {t('houseAllocation.noAssigned')}
          </div>
        ) : (
          students.map((s) => (
            <DraggableStudent
              key={s.uuid}
              student={s}
              studentLookup={studentLookup}
              readOnly={readOnly}
              highlighted={matchUuids.has(s.uuid)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function HouseAllocation() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { userType } = useUserPermissions();

  const queryClient = useQueryClient();
  const isHouseOwner = userType === 'HOUSE_OWNER';
  const canEdit = !isHouseOwner;

  const [selectedCourse, setSelectedCourse] = useState('');
  const [activeGender, setActiveGender] = useState('M');
  const [personTypeFilter, setPersonTypeFilter] = useState('all'); // 'all' | 'student' | 'cinnire' | 'cuntoir'
  const [yearFilter, setYearFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedHouseUuid, setSelectedHouseUuid] = useState(null);
  const [activeStudent, setActiveStudent] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearAcknowledged, setClearAcknowledged] = useState(false);
  const [clearPasswordError, setClearPasswordError] = useState('');
  const [clearLoading, setClearLoading] = useState(false);
  const clearPasswordRef = useRef(null);

  // House download / print state
  const [printData, setPrintData] = useState(null);       // null = overlay closed
  const [downloadLoading, setDownloadLoading] = useState(false);

  const { showAllTime } = useCourseTime();

  // Course summary cards (shown before a course is selected)
  const { data: courseSummary = [] } = useQuery({
    queryKey: ['allocation', 'summary', showAllTime ? 'all' : 'upcoming'],
    queryFn: () => api.allocation.getSummary(showAllTime),
    staleTime: 0,
  });

  // Courses (for the selector dropdown — respects showAllTime)
  const { data: coursesData } = useCourses({}, { staleTime: 5 * 60 * 1000 });
  const courses = useMemo(() => {
    const list = Array.isArray(coursesData) ? coursesData : coursesData?.results ?? [];
    const today = new Date().toISOString().slice(0, 10);
    return list
      .filter((c) => c.uuid && (showAllTime || !c.end_date || c.end_date >= today))
      .sort((a, b) => (a.start_date ?? '').localeCompare(b.start_date ?? ''));
  }, [coursesData, showAllTime]);

  // Allocation data
  const { houses, students, cuntoirs, isLoading, error, moveStudents } =
    useHouseAllocation(selectedCourse);

  // Combined list of all draggable people (students + cúntóirí)
  const allPeople = useMemo(() => [
    ...students,
    ...cuntoirs,
  ], [students, cuntoirs]);

  // Year options derived from all people in this course
  const yearOptions = useMemo(() => {
    const seen = new Map();
    allPeople.forEach((s) => {
      if (s.school_year && !seen.has(s.school_year)) {
        seen.set(s.school_year, s.school_year_order ?? 0);
      }
    });
    return Array.from(seen.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([year]) => ({ year }));
  }, [allPeople]);

  // Switch gender tab — clear search, filters, and selection
  const handleGenderTab = (gender) => {
    setActiveGender(gender);
    setSearch('');
    setPersonTypeFilter('all');
    setYearFilter('all');
    setSelectedHouseUuid(null);
  };

  // Select/deselect a house
  const handleHouseSelect = (uuid) => {
    setSelectedHouseUuid((prev) => (prev === uuid ? null : uuid));
  };

  // Houses split by gender
  const maleHouses = useMemo(
    () => houses.filter((h) => h.gender_designation === 'M'),
    [houses]
  );
  const femaleHouses = useMemo(
    () => houses.filter((h) => h.gender_designation === 'F'),
    [houses]
  );
  const genderFilteredHouses = activeGender === 'M' ? maleHouses : femaleHouses;

  // Unassigned people (students + cúntóirí) — filtered by activeGender + search + personTypeFilter + yearFilter
  const unassignedStudents = useMemo(() => {
    const q = search.toLowerCase();

    // Base: unassigned, correct gender, correct person type
    const baseUnassigned = allPeople.filter((s) => {
      if (s.house_uuid) return false;
      if (s.gender !== activeGender) return false;
      if (personTypeFilter === 'student' && (s.is_cinnire || s.is_cuntoir)) return false;
      if (personTypeFilter === 'cinnire' && !s.is_cinnire) return false;
      if (personTypeFilter === 'cuntoir' && !s.is_cuntoir) return false;
      return true;
    });

    let result;
    let crossYearUuids = new Set();

    if (yearFilter && yearFilter !== 'all') {
      const yearMatchUuids = new Set(
        baseUnassigned.filter((s) => s.school_year === yearFilter).map((s) => s.uuid)
      );
      // Pull in unassigned cross-year people whose friend_group intersects the selected year
      crossYearUuids = new Set(
        baseUnassigned
          .filter((s) => s.school_year !== yearFilter && s.friend_group?.some((u) => yearMatchUuids.has(u)))
          .map((s) => s.uuid)
      );
      result = baseUnassigned.filter((s) => yearMatchUuids.has(s.uuid) || crossYearUuids.has(s.uuid));
    } else {
      result = baseUnassigned;
    }

    if (q) {
      result = result.filter((s) => {
        const name = `${s.first_name} ${s.last_name}`.toLowerCase();
        return name.includes(q) || (s.student_id || '').includes(q);
      });
    }

    // Tag cross-year friends so the card can show a visual indicator
    return result.map((s) => ({ ...s, _crossYearFriend: crossYearUuids.has(s.uuid) }));
  }, [allPeople, search, activeGender, personTypeFilter, yearFilter]);

  // Total unassigned count for the active gender (ignoring search)
  const unassignedCount = useMemo(
    () => allPeople.filter((s) => !s.house_uuid && s.gender === activeGender).length,
    [allPeople, activeGender]
  );

  // Overall progress
  const assignedCount = useMemo(() => allPeople.filter((s) => s.house_uuid).length, [allPeople]);

  const progressStats = useMemo(() => {
    const maleTotal = allPeople.filter((s) => s.gender === 'M').length;
    const femaleTotal = allPeople.filter((s) => s.gender === 'F').length;
    const maleAssigned = allPeople.filter((s) => s.gender === 'M' && s.house_uuid).length;
    const femaleAssigned = allPeople.filter((s) => s.gender === 'F' && s.house_uuid).length;
    return { maleTotal, femaleTotal, maleAssigned, femaleAssigned };
  }, [allPeople]);

  // Lookup map for friend group tooltips: uuid → {first_name, last_name, student_id}
  const studentLookup = useMemo(() => {
    const map = {};
    for (const s of allPeople) {
      map[s.uuid] = { first_name: s.first_name, last_name: s.last_name, student_id: s.student_id || '' };
    }
    return map;
  }, [allPeople]);

  // Selected house object
  const selectedHouse = useMemo(
    () => houses.find((h) => h.uuid === selectedHouseUuid) ?? null,
    [houses, selectedHouseUuid]
  );

  // Per-house people lists (students + cúntóirí)
  const houseStudentMap = useMemo(() => {
    const map = {};
    for (const h of houses) {
      map[h.uuid] = allPeople.filter((s) => s.house_uuid === h.uuid);
    }
    return map;
  }, [allPeople, houses]);

  // Assigned students matching current search (for house grid highlighting)
  const assignedMatchUuids = useMemo(() => {
    if (!search) return new Set();
    const q = search.toLowerCase();
    const matched = new Set();
    for (const person of allPeople) {
      if (!person.house_uuid) continue;
      const name = `${person.first_name} ${person.last_name}`.toLowerCase();
      if (name.includes(q) || (person.student_id || '').includes(q)) {
        matched.add(person.uuid);
      }
    }
    return matched;
  }, [allPeople, search]);

  const houseMatchCounts = useMemo(() => {
    const counts = {};
    for (const h of houses) {
      const people = houseStudentMap[h.uuid] ?? [];
      counts[h.uuid] = people.filter((p) => assignedMatchUuids.has(p.uuid)).length;
    }
    return counts;
  }, [houseStudentMap, assignedMatchUuids, houses]);

  // ── DnD ────────────────────────────────────────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragStart = ({ active }) => {
    setActiveStudent(allPeople.find((s) => s.uuid === active.id) ?? null);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveStudent(null);
    if (!over || !canEdit) return;

    const person = allPeople.find((s) => s.uuid === active.id);
    if (!person) return;

    const targetHouseUuid = over.id === 'unassigned' ? null : over.id;
    if (person.house_uuid === targetHouseUuid) return;

    // Cúntóir — move individually (no friend groups)
    if (person.is_cuntoir) {
      try {
        await moveStudents([], targetHouseUuid, [person.uuid]);
      } catch (err) {
        const detail = err?.response?.data?.details?.[0]?.reason;
        const isGender = detail?.includes('gender') || detail === 'gender';
        toast({
          title: isGender
            ? t('houseAllocation.error.genderMismatch')
            : t('houseAllocation.error.capacityExceeded'),
          description: isGender
            ? t('houseAllocation.error.genderMismatchDesc')
            : t('houseAllocation.error.capacityExceededDesc'),
          variant: 'destructive',
        });
      }
      return;
    }

    const student = person;
    const toMove =
      student.friend_group.length > 0
        ? [student.uuid, ...student.friend_group]
        : [student.uuid];

    try {
      await moveStudents(toMove, targetHouseUuid);
      if (student.friend_group.length > 0) {
        toast({
          title: t('houseAllocation.friendGroupMoved', { count: toMove.length }),
          description: t('houseAllocation.friendGroupMovedDesc'),
          variant: 'success',
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.details?.[0]?.reason;
      const isGender = detail?.includes('gender') || detail === 'gender';
      toast({
        title: isGender
          ? t('houseAllocation.error.genderMismatch')
          : t('houseAllocation.error.capacityExceeded'),
        description: isGender
          ? t('houseAllocation.error.genderMismatchDesc')
          : t('houseAllocation.error.capacityExceededDesc'),
        variant: 'destructive',
      });
    }
  };

  // ── Clear all ──────────────────────────────────────────────────────────────

  const resetClearDialog = () => {
    setClearDialogOpen(false);
    setClearAcknowledged(false);
    setClearPasswordError('');
    setClearLoading(false);
    if (clearPasswordRef.current) clearPasswordRef.current.value = '';
  };

  const handleClearAll = async () => {
    const password = clearPasswordRef.current?.value || '';
    if (!password) return;
    setClearPasswordError('');
    setClearLoading(true);
    try {
      const result = await api.allocation.clear(selectedCourse, password, activeGender);
      await queryClient.invalidateQueries({ queryKey: ['allocation', selectedCourse] });
      await queryClient.invalidateQueries({ queryKey: ['allocation', 'summary'] });
      resetClearDialog();
      setSelectedHouseUuid(null);
      toast({
        title: t('houseAllocation.clearedAll'),
        description: t('houseAllocation.clearedAllCount', { count: result.cleared_count }),
        variant: 'success',
      });
    } catch (err) {
      setClearLoading(false);
      if (err?.response?.status === 403) {
        setClearPasswordError(t('houseAllocation.clearPasswordError'));
      } else {
        toast({ title: t('common.error'), description: t('houseAllocation.clearError'), variant: 'destructive' });
      }
    }
  };

  // ── House download helpers ─────────────────────────────────────────────────

  async function fetchPrintableData() {
    const resp = await apiClient.get(
      `/api/students/allocation/printable/?course=${selectedCourse}`
    );
    return resp.data;
  }

  async function handleOpenPrintView() {
    setDownloadLoading(true);
    try {
      const data = await fetchPrintableData();
      setPrintData(data);
    } catch (err) {
      toast({ title: t('common.error'), description: t('houseAllocation.downloadError', 'Could not load house data'), variant: 'destructive' });
    } finally {
      setDownloadLoading(false);
    }
  }

  async function handleDownloadCSV() {
    setDownloadLoading(true);
    try {
      const data = await fetchPrintableData();
      const courseName = (data.course?.name ?? 'houses').replace(/[^a-zA-Z0-9À-ÿ _-]/g, '_');

      // Build CSV — house metadata row followed by student rows, blank row between houses.
      // Columns: House ID | House Name | Bean an Tí | Phone | Email | Capacity | Occupancy
      //          | Student ID | Full Name | School Year | Friend Group
      // House columns are left blank on student rows to avoid repetition.
      const HOUSE_COLS  = ['House ID', 'House Name', 'Bean an Tí', 'Phone', 'Email', 'Capacity', 'Occupancy'];
      const STUDENT_COLS = ['Student ID', 'Full Name', 'School Year', 'Friend Group'];
      const rows = [[ ...HOUSE_COLS, ...STUDENT_COLS ]];

      for (let i = 0; i < data.houses.length; i++) {
        const house = data.houses[i];

        // One header row per house (house metadata only, student cells blank)
        rows.push([
          house.id, house.name, house.owner_name ?? '', house.owner_phone ?? '',
          house.owner_email ?? '', house.max_capacity, house.current_occupancy,
          '', '', '', '',
        ]);

        // Student rows — house cells left empty
        for (const s of house.students) {
          rows.push([
            '', '', '', '', '', '', '',
            s.student_id, s.full_name, s.school_year,
            s.friend_group_id ? `Grúpa Cairde ${s.friend_group_id}` : '',
          ]);
        }

        // Blank separator row between houses (skip after last)
        if (i < data.houses.length - 1) rows.push([]);
      }

      const csv = rows
        .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${courseName}-houses-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({ title: `${data.houses.length} teach íoslódáilte`, variant: 'success' });
    } catch (err) {
      toast({ title: t('common.error'), description: t('houseAllocation.downloadError', 'Could not download house data'), variant: 'destructive' });
    } finally {
      setDownloadLoading(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Header — only shown once a course is selected */}
      {selectedCourse && (
        <div className="flex items-center justify-between flex-wrap gap-3 flex-shrink-0">
          <div>
            <button
              onClick={() => setSelectedCourse('')}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-1 -ml-1 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              {t('houseAllocation.allCourses')}
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight border-l-4 pl-3" style={{ borderColor: '#F28482' }}>
              {courses.find((c) => c.uuid === selectedCourse)?.name ?? t('houseAllocation.title')}
            </h1>
            {students.length > 0 && (
              <div className="flex items-center gap-4 mt-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{t('houseAllocation.total')}:</span>
                  <span className="text-sm font-medium">{assignedCount} / {students.length}</span>
                  <div className="w-24 h-1.5 rounded-full bg-muted">
                    <div className="h-1.5 rounded-full transition-all" style={{ width: `${students.length > 0 ? (assignedCount / students.length) * 100 : 0}%`, background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }} />
                  </div>
                </div>
                <span className="text-muted-foreground">·</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-blue-600">{t('houseAllocation.male')}:</span>
                  <span className="text-sm font-medium">{progressStats.maleAssigned} / {progressStats.maleTotal}</span>
                  <div className="w-20 h-1.5 rounded-full bg-blue-100 dark:bg-blue-900/40">
                    <div className="h-1.5 rounded-full bg-blue-500 transition-all" style={{ width: `${progressStats.maleTotal > 0 ? (progressStats.maleAssigned / progressStats.maleTotal) * 100 : 0}%` }} />
                  </div>
                </div>
                <span className="text-muted-foreground">·</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-pink-600">{t('houseAllocation.female')}:</span>
                  <span className="text-sm font-medium">{progressStats.femaleAssigned} / {progressStats.femaleTotal}</span>
                  <div className="w-20 h-1.5 rounded-full bg-pink-100 dark:bg-pink-900/40">
                    <div className="h-1.5 rounded-full bg-pink-500 transition-all" style={{ width: `${progressStats.femaleTotal > 0 ? (progressStats.femaleAssigned / progressStats.femaleTotal) * 100 : 0}%` }} />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-52">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                placeholder={t('houseAllocation.searchStudents')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-8 text-xs"
              />
            </div>
            {/* Download / Print house list */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenPrintView}
              disabled={downloadLoading || !houses.length}
              title="Priontáil liosta tithe"
            >
              {downloadLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              ) : (
                <Printer className="h-4 w-4 mr-1.5" />
              )}
              {t('houseAllocation.printHouses', 'Print Houses')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadCSV}
              disabled={downloadLoading || !houses.length}
              title="Íoslódáil CSV"
            >
              {downloadLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              ) : (
                <Download className="h-4 w-4 mr-1.5" />
              )}
              CSV
            </Button>
            {canEdit && students.some((s) => s.house_uuid && s.gender === activeGender) && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive border-destructive hover:bg-destructive/10"
                onClick={() => setClearDialogOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {activeGender === 'M' ? t('houseAllocation.clearMale') : t('houseAllocation.clearFemale')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* House print overlay */}
      {printData && (
        <HousePrintView data={printData} onClose={() => setPrintData(null)} />
      )}

      {/* Empty state — course selection cards */}
      {!selectedCourse && (
        <div className="flex-1 overflow-y-auto">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight border-l-4 pl-3" style={{ borderColor: '#F28482' }}>{t('houseAllocation.title')}</h1>
              <p className="text-muted-foreground mt-1 pl-3">{t('houseAllocation.selectCoursePrompt')}</p>
            </div>
            <CourseTimeToggle />
          </div>
          {(() => {
            const n = courseSummary.length;
            const cols =
              n <= 2 ? 2 :
              n === 3 ? 3 :
              n === 4 ? 2 :
              n <= 6 ? 3 :
              n <= 8 ? 4 : 4;
            const gridClass =
              cols === 2 ? 'grid-cols-1 sm:grid-cols-2 max-w-3xl' :
              cols === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl' :
                           'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 max-w-7xl';
            return (
          <div className={`grid gap-5 mx-auto w-full ${gridClass}`}>
            {courseSummary.map((course) => {
              const pct = course.total > 0 ? (course.assigned / course.total) * 100 : 0;
              const malePct = course.male_total > 0 ? (course.male_assigned / course.male_total) * 100 : 0;
              const femalePct = course.female_total > 0 ? (course.female_assigned / course.female_total) * 100 : 0;
              const isComplete = course.total > 0 && course.assigned === course.total;

              return (
                <button
                  key={course.uuid}
                  onClick={() => setSelectedCourse(course.uuid)}
                  className="text-left rounded-xl border bg-card p-6 hover:border-blue-400 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-2 mb-4">
                    <div className="font-semibold text-lg leading-tight">{course.name}</div>
                    {isComplete && (
                      <span className="text-xs bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/40 dark:text-green-400 dark:border-green-700 rounded-full px-2 py-0.5 flex-shrink-0">
                        {t('houseAllocation.complete')}
                      </span>
                    )}
                  </div>

                  {/* Overall progress */}
                  <div className="space-y-1.5 mb-4">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>{t('houseAllocation.total')}</span>
                      <span className="font-medium text-foreground">{course.assigned} / {course.total}</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-muted">
                      <div
                        className={`h-2.5 rounded-full transition-all ${isComplete ? 'bg-green-500' : ''}`}
                        style={{ width: `${pct}%`, ...(isComplete ? {} : { background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }) }}
                      />
                    </div>
                  </div>

                  {/* Male / Female breakdown */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-blue-600 w-14 flex-shrink-0">{t('houseAllocation.male')}</span>
                      <div className="flex-1 h-2 rounded-full bg-blue-100 dark:bg-blue-900/40">
                        <div className="h-2 rounded-full bg-blue-500 transition-all" style={{ width: `${malePct}%` }} />
                      </div>
                      <span className="text-sm text-muted-foreground w-16 text-right">{course.male_assigned}/{course.male_total}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-pink-600 w-14 flex-shrink-0">{t('houseAllocation.female')}</span>
                      <div className="flex-1 h-2 rounded-full bg-pink-100 dark:bg-pink-900/40">
                        <div className="h-2 rounded-full bg-pink-500 transition-all" style={{ width: `${femalePct}%` }} />
                      </div>
                      <span className="text-sm text-muted-foreground w-16 text-right">{course.female_assigned}/{course.female_total}</span>
                    </div>
                  </div>

                  {/* House count */}
                  <div className="mt-4 pt-4 border-t text-sm text-muted-foreground">
                    {t('houseAllocation.houseCount', { count: course.house_count })}
                  </div>
                </button>
              );
            })}
          </div>
            );
          })()}
        </div>
      )}

      {/* Clear All inline confirmation panel */}
      {clearDialogOpen && (
        <div className="rounded-lg border border-destructive bg-destructive/5 p-4 space-y-4 flex-shrink-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-semibold text-destructive">
                {activeGender === 'M' ? t('houseAllocation.clearMale') : t('houseAllocation.clearFemale')}
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">
                {activeGender === 'M' ? t('houseAllocation.clearMaleConfirm') : t('houseAllocation.clearFemaleConfirm')}
              </p>
            </div>
            <button onClick={resetClearDialog} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Acknowledgement checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-destructive"
              checked={clearAcknowledged}
              onChange={(e) => setClearAcknowledged(e.target.checked)}
            />
            <span className="text-sm">{t('houseAllocation.clearAcknowledge')}</span>
          </label>

          {/* Password */}
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('houseAllocation.clearPassword')}</label>
            <div className="flex items-center gap-2">
              <Input
                ref={clearPasswordRef}
                type="password"
                name="current-password"
                autoComplete="current-password"
                placeholder={t('houseAllocation.clearPasswordPlaceholder')}
                onChange={() => setClearPasswordError('')}
                className="max-w-xs"
              />
              <Button
                variant="outline"
                onClick={resetClearDialog}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleClearAll}
                disabled={!clearAcknowledged || clearLoading}
              >
                {clearLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-2 h-4 w-4" />
                )}
                {t('houseAllocation.clearAll')}
              </Button>
            </div>
            {clearPasswordError && (
              <p className="text-xs text-destructive">{clearPasswordError}</p>
            )}
          </div>
        </div>
      )}

      {/* HOUSE_OWNER read-only banner */}
      {isHouseOwner && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 px-4 py-2 text-sm text-amber-800 dark:text-amber-300 flex-shrink-0">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {t('houseAllocation.readOnlyBanner')}
        </div>
      )}

      {/* Loading */}
      {selectedCourse && isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Error */}
      {selectedCourse && error && (
        <div className="text-center py-12 text-destructive">{t('common.errorLoading')}</div>
      )}

      {/* Board */}
      {selectedCourse && !isLoading && !error && (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="flex flex-col md:flex-row gap-4 flex-1 min-h-0">

            {/* Left: Unassigned panel — hidden on mobile for read-only House Owners */}
            <div className={isHouseOwner ? 'hidden md:contents' : 'contents'}>
              <UnassignedPanel
                students={unassignedStudents}
                totalCount={unassignedCount}
                personTypeFilter={personTypeFilter}
                onPersonTypeFilter={setPersonTypeFilter}
                allPeople={allPeople}
                activeGender={activeGender}
                readOnly={isHouseOwner}
                studentLookup={studentLookup}
                yearFilter={yearFilter}
                onYearFilter={setYearFilter}
                yearOptions={yearOptions}
              />
            </div>

            {/* Right: Gender tabs + house grid */}
            <div className="flex-1 flex flex-col min-w-0">

              {/* Gender tabs */}
              <div className="flex gap-2 mb-4 flex-shrink-0">
                <button
                  onClick={() => handleGenderTab('M')}
                  className={`px-4 py-3 rounded-lg text-sm font-medium border transition-colors ${
                    activeGender === 'M'
                      ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                      : 'bg-card border-border text-muted-foreground hover:bg-muted/30'
                  }`}
                >
                  {t('houseAllocation.maleHouses')} ({maleHouses.length})
                </button>
                <button
                  onClick={() => handleGenderTab('F')}
                  className={`px-4 py-3 rounded-lg text-sm font-medium border transition-colors ${
                    activeGender === 'F'
                      ? 'bg-pink-50 border-pink-300 text-pink-700 dark:bg-pink-900/30 dark:border-pink-700 dark:text-pink-300'
                      : 'bg-card border-border text-muted-foreground hover:bg-muted/30'
                  }`}
                >
                  {t('houseAllocation.femaleHouses')} ({femaleHouses.length})
                </button>
              </div>

              {/* House grid */}
              <div className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 pb-4">
                  {genderFilteredHouses.map((house) => (
                    <HouseCard
                      key={house.uuid}
                      house={house}
                      students={houseStudentMap[house.uuid] ?? []}
                      isSelected={selectedHouseUuid === house.uuid}
                      onSelect={() => handleHouseSelect(house.uuid)}
                      readOnly={isHouseOwner}
                      matchCount={houseMatchCounts[house.uuid] ?? 0}
                      searchActive={!!search}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Right: House detail panel */}
            {selectedHouse && (
              <HouseDetailPanel
                house={selectedHouse}
                students={houseStudentMap[selectedHouse.uuid] ?? []}
                onClose={() => setSelectedHouseUuid(null)}
                readOnly={isHouseOwner}
                studentLookup={studentLookup}
                searchQuery={search}
              />
            )}
          </div>

          {/* Drag overlay */}
          <DragOverlay>
            {activeStudent && <StudentCard student={activeStudent} studentLookup={studentLookup} />}
          </DragOverlay>
        </DndContext>
      )}

    </div>
  );
}

export default HouseAllocation;
